import classNames from "classnames";
import * as _ from "lodash";
import { useCallback, useMemo } from "react";

import refresh from "@/assets/refresh-health.svg";
import CustomNavDropdown from "@/components/features/map-container/top-bar/CustomNavDropdown";
import type Light from "@/models/light/light";
import type MapSettings from "@/models/map-settings";
import { isNotCommissioned } from "@/services/light";
import { Map } from "@/utils";

interface HealthyPanelProps {
    lights?: Light[];
    mapSettings?: Partial<MapSettings>;
    duplicateCheck: () => void;
    timeStamp: string;
}

const clusterFlags = (positioningType: string) => {
    let flags;
    switch (positioningType) {
        case "BLE":
            flags = _.filter(
                Map.Collections.flags,
                (flag) =>
                    flag.label !== "Highlighted" &&
                    flag.label !== "Duplicate VLC ID" &&
                    flag.label !== "Not LED" &&
                    flag.label !== "Out of Scope" &&
                    flag.label !== "No BLE UUID" &&
                    flag.label !== "No VLC Signal",
            );
            break;
        case "VLC":
            flags = _.filter(
                Map.Collections.flags,
                (flag) =>
                    flag.label !== "Highlighted" &&
                    flag.label !== "Duplicate VLC ID" &&
                    flag.label !== "Not LED" &&
                    flag.label !== "Duplicate BLE ID" &&
                    flag.label !== "No Primary BLE ID" &&
                    flag.label !== "No BLE UUID" &&
                    flag.label !== "Out of Scope",
            );
            break;
        default:
            flags = _.filter(
                Map.Collections.flags,
                (flag) =>
                    flag.label !== "Highlighted" &&
                    flag.label !== "Duplicate VLC ID" &&
                    flag.label !== "Not LED" &&
                    flag.label !== "No BLE UUID" &&
                    flag.label !== "Out of Scope",
            );
            break;
    }
    return flags;
};

const hasClusterFlag = (light: Light, positioningType: string) =>
    _.some(light.flags, (selectedFlag) =>
        _.some(
            clusterFlags(positioningType),
            (clusterFlag) => clusterFlag.value === selectedFlag.value,
        ),
    );

/**
 * Analyzes lights for cluster and duplicate defects and displays health information.
 *
 * @param root0 - The component props.
 * @param root0.lights - The lights map to analyze.
 * @param root0.mapSettings - The map settings for grid calculations.
 * @param root0.duplicateCheck - The duplicate check results.
 * @param root0.timeStamp - The timestamp of the last health check.
 * @returns The rendered healthy panel.
 */
const HealthyPanel = ({
    lights = [],
    mapSettings = {},
    duplicateCheck,
    timeStamp,
}: HealthyPanelProps) => {
    const getClusterCount = useCallback(
        (lightsArr) => {
            const positioningType =
                !_.isEmpty(mapSettings.fixtureDetails) &&
                mapSettings.fixtureDetails.positioningType;
            const defectiveLights = [];
            const clusterProximityLimitInMeters = 4;
            const clusters = [];
            let defectiveChainedLights = [];

            const getClosestDefectiveLights = (defectiveLight) => {
                const indexOfDefectiveLight =
                    defectiveLights.indexOf(defectiveLight);
                const otherDefectiveLights = [
                    ...defectiveLights.slice(0, indexOfDefectiveLight),
                    ...defectiveLights.slice(indexOfDefectiveLight + 1),
                ];
                return _.filter(
                    otherDefectiveLights,
                    (otherDefectiveLight) =>
                        Map.distanceBetweenPoints(
                            defectiveLight.point,
                            otherDefectiveLight.point,
                        ) <= clusterProximityLimitInMeters,
                );
            };

            const belongToCluster = (defectiveLight) => {
                for (const cluster of clusters) {
                    const indexOf = cluster.indexOf(defectiveLight);
                    if (indexOf > -1) {
                        return true;
                    }
                }
                return false;
            };

            const loadDefectiveChainedLights = (defectiveLight, index = 0) => {
                const closestDefectiveLights =
                    getClosestDefectiveLights(defectiveLight);
                for (const closestDefectiveLight of closestDefectiveLights) {
                    if (
                        !belongToCluster(closestDefectiveLight) &&
                        defectiveChainedLights.indexOf(
                            closestDefectiveLight,
                        ) === -1
                    ) {
                        defectiveChainedLights.push(closestDefectiveLight);
                        loadDefectiveChainedLights(
                            closestDefectiveLight,
                            index + 1,
                        );
                    }
                }
            };

            for (const light of lightsArr) {
                if (
                    light.flags.length > 0 &&
                    hasClusterFlag(light, positioningType)
                ) {
                    defectiveLights.push(light);
                }
            }
            for (const defectiveLight of defectiveLights) {
                if (!belongToCluster(defectiveLight)) {
                    defectiveChainedLights = [defectiveLight];
                    loadDefectiveChainedLights(defectiveLight);
                    if (defectiveChainedLights.length >= 4) {
                        clusters.push(defectiveChainedLights);
                    }
                }
            }

            return clusters.length;
        },
        [mapSettings.fixtureDetails],
    );

    const heartBeatSummary = useMemo(() => {
        const totalLights = lights.length;
        const positioningType =
            !_.isEmpty(mapSettings.fixtureDetails) &&
            mapSettings.fixtureDetails.positioningType;

        const summary = {
            healthPercentage: 100,
            healthyLights: totalLights,
            defectedLights: 0,
            criticalIssues: 0,
            notCommissioned: 0,
            ignoredFlags: 0,
            noBLE: 0,
            lightOutage: 0,
            noVLC: 0,
            partialOutage: 0,
            weakAntenna: 0,
            damaged: 0,
            highlighted: 0,
            notLED: 0,
            outScope: 0,
            duplicateVLC: 0,
            dublicateBLE: 0,
            missingLocationDescription: 0,
            emergencyLight: 0,
        };

        const flags: Light["flags"] = [];

        _.forEach(lights, (l) => {
            if (l.flags.length > 0) {
                flags.push(...l.flags);
            }

            const selectedFlags = l.flags.filter((o) => {
                if (positioningType === "VLC")
                    return (
                        o.value !== 32 && o.value !== 128 && o.value !== 2048
                    );
                if (positioningType === "BLE")
                    return o.value !== 2 && o.value !== 16;
                return true;
            });

            if (isNotCommissioned(l)) {
                summary.notCommissioned += 1;
                summary.defectedLights += 1;
                l.defectLight = true;
            }

            const defected =
                !l.defectLight &&
                (_.some(selectedFlags, ["group", "critical"]) ||
                    _.some(
                        selectedFlags,
                        (f) => f.group === "system" && !f.ignored,
                    ));

            if (defected) {
                summary.defectedLights += 1;
            }

            if (l.isCLS) {
                summary.emergencyLight += 1;
                if (!l.hasLocationDescription) {
                    summary.missingLocationDescription += 1;
                }
            }
        });

        const groupSummary = _.map(
            _.countBy(flags, (o) => o.group),
            (val, key) => ({ group: key, total: val }),
        );

        const valueSummary = _.map(
            _.countBy(flags, (o) => o.value),
            (val, key) => ({ key: key, total: val }),
        );

        if (!_.isEmpty(groupSummary) && !_.isEmpty(valueSummary)) {
            _.forEach(valueSummary, (o) => {
                if (o.key === "128" && positioningType === "VLC + BLE")
                    summary.noBLE = o.total;
                if (o.key === "4") summary.lightOutage = o.total;
                if (
                    o.key === "2" &&
                    (positioningType === "VLC" ||
                        positioningType === "VLC + BLE")
                )
                    summary.noVLC = o.total;
                if (o.key === "64") summary.partialOutage = o.total;
                if (o.key === "256") summary.highlighted = o.total;
                if (o.key === "1") summary.notLED = o.total;
                if (o.key === "1024") summary.outScope = o.total;
                if (o.key === "8") summary.damaged = o.total;
                if (o.key === "512") summary.weakAntenna = o.total;
                if (
                    o.key === "16" &&
                    (positioningType === "VLC" ||
                        positioningType === "VLC + BLE")
                )
                    summary.duplicateVLC = o.total;
                if (
                    o.key === "32" &&
                    (positioningType === "BLE" ||
                        positioningType === "VLC + BLE")
                )
                    summary.dublicateBLE = o.total;
            });
        }

        summary.criticalIssues =
            summary.lightOutage +
            summary.partialOutage +
            summary.notCommissioned +
            summary.damaged +
            summary.weakAntenna +
            summary.missingLocationDescription;

        if (positioningType === "BLE" || positioningType === "VLC + BLE") {
            summary.criticalIssues += summary.dublicateBLE + summary.noBLE;
        }

        if (positioningType === "VLC") {
            summary.criticalIssues += summary.noVLC;
        }

        summary.ignoredFlags =
            summary.highlighted +
            summary.notLED +
            summary.outScope +
            summary.duplicateVLC +
            summary.emergencyLight;

        summary.healthyLights = totalLights - summary.defectedLights;

        summary.healthPercentage = _.ceil(
            (summary.healthyLights / totalLights) * 100,
            1,
        );

        if (summary.healthPercentage < 0) summary.healthPercentage = 0;
        if (summary.healthyLights < 0) summary.healthyLights = 0;

        return summary;
    }, [lights, mapSettings.fixtureDetails]);

    const renderHeartBeatTitle = (healthPercentage: number) => {
        let statusColor;
        if (healthPercentage >= 98) {
            statusColor = "sucess";
        } else if (healthPercentage <= 79) {
            statusColor = "failure";
        } else {
            statusColor = "average";
        }

        return (
            <span className={`heartBeat ${statusColor}`}>
                <i style={{ fontWeight: 500 }} className="fal fa-heart-rate" />
                <span style={{ marginLeft: 10 }}>
                    {healthPercentage > 0 ? healthPercentage : 0} %
                </span>
            </span>
        );
    };

    const onReRunClick = useCallback(() => {
        duplicateCheck();
        renderHeartBeatTitle(heartBeatSummary.healthPercentage);
    }, [duplicateCheck, heartBeatSummary.healthPercentage]);

    const getFlags = (descriptions: Record<string, string | string[]>) =>
        _.map(heartBeatSummary, (count, key) => {
            let description = descriptions[key];
            if (!description || !count || count <= 0) {
                return null;
            }
            let icon;
            let iconColorProp;
            if (description instanceof Array) {
                const descriptionArr = description;
                description = descriptionArr[0];
                icon = {
                    type: descriptionArr[1],
                    color: descriptionArr[2] ? descriptionArr[2] : "#000",
                };
                iconColorProp =
                    icon.type === "triangle"
                        ? "borderBottomColor"
                        : "backgroundColor";
            }
            return (
                <div className="label" key={_.camelCase(description)}>
                    <div className="description">
                        {icon && (
                            <div
                                className={classNames(icon.type, "icon")}
                                style={{ [iconColorProp]: icon.color }}
                            />
                        )}
                        {description}
                    </div>
                    =<div className="quantity">{count}</div>
                </div>
            );
        });

    const critical = {
        notCommissioned: "Not Commissioned",
        lightOutage: ["Full Outage", "triangle", "#09907e"],
        noBLE: ["No Primary BLE ID", "circle", "#fb7daf"],
        noVLC: ["No VLC Signal", "triangle", "#aaf0ff"],
        partialOutage: ["Partial Outage", "triangle", "#52e0cd"],
        damaged: ["Damaged", "triangle", "#3a5068"],
        weakAntenna: ["Week Antenna", "triangle", "#9260ea"],
        dublicateBLE: ["Duplicate BLE ID", "triangle", "#b53939"],
        missingLocationDescription: [
            "Missing Location Description",
            "triangle",
            "#dd8139",
        ],
    };
    const ignored = {
        highlighted: ["Highlighted", "circle", "#b2ad92"],
        notLED: ["Not LED", "circle", "#363636"],
        outScope: ["Out of Scope", "circle", "#f20505"],
        duplicateVLC: ["Duplicate VLC ID", "circle", "#fb7daf"],
        emergencyLight: ["Emergency Light", "circle", "#61bb46"],
    };

    return (
        <CustomNavDropdown
            title={renderHeartBeatTitle(heartBeatSummary.healthPercentage)}
            className="heartbeat-menu"
        >
            <div className="heartbeat-menu-dropdown">
                <div className="title">
                    <span className="left">
                        <div className="total">
                            Healthy Lights: {heartBeatSummary.healthPercentage}%
                            ({heartBeatSummary.healthyLights}/{lights.length})
                        </div>
                        <div className="cluster">
                            Problem Clusters: {getClusterCount(lights)}
                        </div>
                    </span>
                    <div className="right">Total Lights: {lights.length}</div>
                </div>
                <div className="container">
                    <div className="row" style={{ padding: "0 0.5rem" }}>
                        <div className="col-6">
                            <span className="subtitle label">
                                <div className="description">
                                    Critical Issues
                                </div>
                                :
                                <div className="quantity">
                                    {heartBeatSummary.criticalIssues}
                                </div>
                            </span>
                            {getFlags(critical)}
                        </div>
                        <div className="col-6">
                            <div className="subtitle label">
                                <div className="description">Ignored Flags</div>
                                :
                                <div className="quantity">
                                    {heartBeatSummary.ignoredFlags}
                                </div>
                            </div>
                            {getFlags(ignored)}
                        </div>
                    </div>
                </div>
                <div className="footer">
                    <div className="timestamp">Last Checked: {timeStamp}</div>
                    <div className="rerun" onClick={() => onReRunClick()}>
                        <img src={refresh} /> Rerun
                    </div>
                </div>
            </div>
        </CustomNavDropdown>
    );
};

export default HealthyPanel;
